import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { WebsiteStructuredData } from '../components/StructuredData';
import CategoryCard from '../components/CategoryCard';
import { fetchCategories } from '../services/api';
import { proxiedImage, onImgError } from '../utils/images';
import { Category } from '../types';

const parentOf = (c: Category): Category['id'] | null =>
  (c.parent_id ?? c.parentId ?? null);

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategories().then((data) => { setCategories(data); setLoading(false); })
      .catch(() => { setError('Failed to load categories'); setLoading(false); });
  }, []);

  const childrenOf = (id: Category['id']) =>
    categories.filter((c) => parentOf(c) != null && String(parentOf(c)) === String(id));
  const roots = categories.filter((c) => parentOf(c) == null);

  return (
    <div className="min-h-screen">
      <SEO title="Browse Categories" description="Explore our curated categories of health-conscious products, vetted for clean ingredients."
        image="/og-image.png" url="https://goodcart.org/categories" type="website" siteName="GoodCart" />
      <WebsiteStructuredData />

      {/* Compact header, consistent with category pages */}
      <section className="bg-sage-700 text-cream-50">
        <div className="container-custom py-6 md:py-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <p className="eyebrow text-sage-200 mb-2">Browse</p>
            <h1 className="text-2xl md:text-3xl font-display text-cream-50">Every category</h1>
            <p className="mt-2 max-w-2xl text-sm text-sage-100/90">Curated categories of health-conscious products, vetted for quality and clean ingredients.</p>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-8 md:py-10">
        {error && <div className="p-4 bg-clay-50 text-clay-700 rounded-xl mb-6 text-center">{error}</div>}
        {loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : (
          <>
            {/* Root categories: 2-column large cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-12">
              {roots.map((root, i) => (
                <motion.div
                  key={root.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: Math.min(i * 0.06, 0.2) }}
                >
                  <Link to={`/categories/${root.slug}`} className="block group organic-card overflow-hidden">
                    <div className="relative h-56 md:h-64 overflow-hidden bg-gradient-to-br from-sage-300 via-sage-200 to-cream-200">
                      {root.image && (
                        <img src={proxiedImage(root.image)} alt={root.name} loading="lazy" decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                          onError={onImgError()} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-earth-900/85 via-earth-900/25 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h2 className="font-display text-3xl font-semibold text-cream-50 leading-tight drop-shadow-md">{root.name}</h2>
                        <p className="text-sm text-cream-200 mt-1 inline-flex items-center gap-1">
                          Shop all {root.name} <span aria-hidden>&rarr;</span>
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Sub-category sections per root */}
            <div className="space-y-14">
              {roots.map((root) => {
                const kids = childrenOf(root.id);
                if (kids.length === 0) return null;
                return (
                  <section key={root.id}>
                    <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
                      <h2 className="text-earth-900 text-xl">{root.name}</h2>
                      <Link to={`/categories/${root.slug}`} className="text-sage-700 font-semibold hover:text-sage-900 whitespace-nowrap inline-flex items-center gap-1 text-sm">
                        Shop all {root.name} <span aria-hidden>&rarr;</span>
                      </Link>
                    </div>
                    <div className="organic-divider mb-6"></div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                      {kids.map((c, i) => {
                        const grandkids = childrenOf(c.id).length;
                        return (
                          <CategoryCard key={c.id} category={c} index={i}
                            badge={grandkids > 0 ? `${grandkids} sub-categories` : undefined} />
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
